<template>
  <div
    v-if="!readOnly || volatilitySlugIsValid"
    class="volatility-rating"
    :class="[
      volatilitySlugClass,
      { 'volatility-rating--selectable': !readOnly }
    ]"
  >
    <div class="volatility-rating__container">
      <div
        v-if="showTitle && volatilityTitle && volatilityTitle.length > 0"
        class="volatility-rating__title"
      >
        {{ volatilityTitle }}
      </div>
      <div class="volatility-rating__bar-wrapper">
        <span class="volatility-rating__bar" />
        <template v-for="option in volatilityMapOptions">
          <div
            class="volatility-rating__dot"
            :class="[
              volatilityDotClass(option),
              { 'volatility-rating__dot--active': option === volatilitySlug }
            ]"
            @click="setVolatilityRating(option)"
          />
        </template>
      </div>
      <div class="volatility-rating__name">{{ volatilityName }}</div>
    </div>
    <div
      v-if="showDescription"
      class="volatility-rating__description"
    >
      {{ volatilityDescription }}
    </div>
  </div>
</template>

<script>
import { keys } from 'lodash'

export default {
  name: 'VolatilityRating',
  components: {},
  data () {
    return {
      volatilityMap: {
        'casual': {
          'name': 'Casual',
          'description': 'Focus is on more frequent pays for longer play sessions. Allows for full immersion and more opportunities to trigger bonus features.',
        },
        'casual-balanced': {
          'name': 'Casual Balanced',
          'description': 'Frequent pays and moderately higher payout amounts than casual games',
        },
        'balanced': {
          'name': 'Balanced',
          'description': 'Blended games that can either be a jumping point to more aggressive ones or the perfect mix of hit frequency and payout.',
        },
        'balanced-aggressive': {
          'name': 'Balanced Aggressive',
          'description': 'Focus is on more opportunities to win large awards with a moderate hit frequency.',
        },
        'aggressive': {
          'name': 'Aggressive',
          'description': 'Aggressive games provide the best chance for winning big awards.',
        },
      }
    }
  },
  props: {
    volatilityTitle: {
      default: 'Volatility Meter',
      type: String
    },
    volatilitySlug: String,
    showTitle: {
      default: true,
      type: Boolean
    },
    showDescription: {
      default: false,
      type: Boolean
    },
    readOnly: {
      default: true,
      type: Boolean
    }
  },
  computed: {
    volatilitySlugClass: function () {
      var self = this
      return self.volatilitySlug ? 'volatility-rating--' + self.volatilitySlug : null
    },
    volatilitySlugIsValid: function () {
      var self = this
      return self.volatilitySlug !== null && self.volatilityMap.hasOwnProperty(self.volatilitySlug)
    },
    volatilityName: function () {
      var self = this
      return self.volatilitySlugIsValid ? self.volatilityMap[self.volatilitySlug].name : ''
    },
    volatilityMapOptions: function () {
      var self = this
      return keys(self.volatilityMap)
    },
    volatilityDescription: function () {
      var self = this
      return self.volatilitySlugIsValid ? self.volatilityMap[self.volatilitySlug].description : ''
    },
  },
  created: function () {
  },
  mounted: function () {
  },
  methods: {
    volatilityDotClass: function (option) {
      var self = this
      return 'volatility-rating__dot--' + option
    },
    setVolatilityRating: function (volatilitySlug) {
      var self = this
      if (!self.readOnly) {
        self.$emit('volatilityChanged', volatilitySlug)
      }
    }
  }
}
</script>

<style lang="scss">
@import "src/scss/toolkit";

$volatility-casual: #62b741 !default;
$volatility-casual-balanced: #b6b91f !default;
$volatility-balanced: #fdba01 !default;
$volatility-balanced-aggressive: #f4841b !default;
$volatility-aggressive: #e53e3c !default;

$c: ".volatility-rating";
#{$c} {
  display: inline-block;

  &__container {
    width: 160px;
  }

  &__title {
    @include extend(h6);
    margin-bottom: 6px;
  }

  &__bar-wrapper {
    position: relative;
    padding-top: 6px;
    padding-bottom: 6px;
    margin-bottom: 2px;
  }

  &__bar {
    display: block;
    width: 100%;
    height: 4px;
    border-radius: 99999px;
    background: linear-gradient(
        to right,
        $volatility-casual,
        $volatility-casual-balanced,
        $volatility-balanced,
        $volatility-balanced-aggressive,
        $volatility-aggressive
    );
  }

  &__dot {
    position: absolute;
    display: block;
    width: 16px;
    height: 16px;
    top: 50%;
    left: 0;
    border: 2px solid palette(white);
    border-radius: 50%;
    transform: translate(-50%, -50%);
    opacity: 0;
    z-index: 1;

    @include css-variables {
      transition: all calc(1s * #{var(base-trans-speed)});
    }

    #{$c}--selectable & {
      cursor: pointer;
    }

    #{$c}--selectable #{$c}__bar-wrapper & {
      opacity: 0.25;
    }

    #{$c}--selectable #{$c}__bar-wrapper &:hover {
      opacity: 0.75;
    }

    &--active {
      opacity: 1 !important;
    }

    &--casual {
      background-color: $volatility-casual;
    }

    &--casual-balanced {
      background-color: $volatility-casual-balanced;
      left: 25%;
    }

    &--balanced {
      background-color: $volatility-balanced;
      left: 50%;
    }

    &--balanced-aggressive {
      background-color: $volatility-balanced-aggressive;
      left: 75%;
    }

    &--aggressive {
      background-color: $volatility-aggressive;
      left: 100%;
    }
  }

  &__name {
    @include extend(h6);

    #{$c}--casual & {
      color: $volatility-casual;
    }

    #{$c}--casual-balanced & {
      color: $volatility-casual-balanced;
    }

    #{$c}--balanced & {
      color: $volatility-balanced;
      text-align: center;
    }

    #{$c}--balanced-aggressive & {
      color: $volatility-balanced-aggressive;
      text-align: right;
    }

    #{$c}--aggressive & {
      color: $volatility-aggressive;
      text-align: right;
    }
  }

  &__description {
    font-size: 14px;
    margin-top: 24px;
    color: palette(grey);
  }
}

</style>



// WEBPACK FOOTER //
// src/components/molecules/VolatilityRating.vue